// RandomPageButton — header Easter egg. One click drops the reader on a
// random marked place across the site. The pool is filtered through the
// speakeasy gate (entries flagged speakeasy: true are excluded unless the
// reader has crossed the door, signaled by the
// ktp.speakeasy.entered.v1 localStorage flag).
//
// Selection avoids the current path (5-attempt retry). Internal routes go
// through wouter's setLocation; external URLs leave via window.location
// so the reader is sent to the actual essay or RFC.

import { useLocation } from "wouter";
import { MARKS } from "../data/marked-places";

const ENTERED_KEY = "ktp.speakeasy.entered.v1";

function readDoor(): boolean {
  try {
    return localStorage.getItem(ENTERED_KEY) === "1";
  } catch {
    return false;
  }
}

function pickRandom(currentPath: string): {
  href: string;
  external: boolean;
} | null {
  const open = readDoor();
  const pool = MARKS.filter((m) => (open ? true : !m.speakeasy));
  if (pool.length === 0) return null;

  for (let i = 0; i < 5; i++) {
    const pick = pool[Math.floor(Math.random() * pool.length)];
    // Avoid landing on the current path. External URLs always count as
    // "different" so they always pass.
    if (pick.external || pick.href !== currentPath) {
      return { href: pick.href, external: !!pick.external };
    }
  }
  // Fallback after 5 attempts — accept whatever.
  const fallback = pool[Math.floor(Math.random() * pool.length)];
  return { href: fallback.href, external: !!fallback.external };
}

export default function RandomPageButton() {
  const [location, setLocation] = useLocation();

  const onClick = () => {
    const pick = pickRandom(location);
    if (!pick) return;
    if (pick.external) {
      window.location.assign(pick.href);
    } else {
      setLocation(pick.href);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Wander to a random place on the site"
      title="Wander → random place"
      // The same box as SearchButton beside it: both chips stand 44px tall (Chris, 2026-10-29).
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 44,
        boxSizing: "border-box",
        background: "transparent",
        border: "1px solid var(--line)",
        padding: "4px 10px",
        fontFamily: "var(--mono)",
        fontSize: 11,
        letterSpacing: 0.5,
        textTransform: "lowercase",
        color: "var(--ink-3)",
        cursor: "pointer",
        transition:
          "color 180ms var(--ease-out-soft), border-color 180ms var(--ease-out-soft)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "var(--ink)";
        e.currentTarget.style.borderColor = "var(--ink)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "var(--ink-3)";
        e.currentTarget.style.borderColor = "var(--line)";
      }}
    >
      wander →
    </button>
  );
}
